import { Overline } from "@/components/landing/Shared";

const PREAMBLE = [
  "Welcome to Lawsuit Settlement Help. The website you are visiting, lawsuitsettlementhelp.com (the “Website”), is owned and operated by Lawsuit Settlement Help. These Terms of Use (“Terms”) govern your access to and use of the Website.",
  "Any use of this Website is subject to these Terms of Use, together with our Disclaimer and Privacy Policy. By accessing or using the Website, submitting an inquiry, or providing information through any form on the Website, you acknowledge that you have read, understood, and agree to all of them. Please read them carefully.",
  "IF YOU USE THIS WEBSITE, YOU ARE DEEMED TO HAVE READ, UNDERSTOOD, AND AGREED TO THESE TERMS OF USE, THE DISCLAIMER, AND THE PRIVACY POLICY. IF YOU DO NOT ACCEPT THEM, YOU SHOULD STOP USING THE WEBSITE OR ANY MATERIALS OBTAINED FROM IT.",
];

const SECTIONS = [
  {
    title: "About the Website",
    paragraphs: [
      "Lawsuit Settlement Help is an informational and lead-generation website.",
      "The Website allows visitors to submit information regarding potential legal claims, settlements, investigations, or other matters that may be of interest to them.",
      "Lawsuit Settlement Help is not a law firm and does not provide legal advice or legal representation.",
      "Information provided through the Website is for general informational and lead-generation purposes only and should not be considered legal advice.",
    ],
  },
  {
    title: "Inquiries and Lead Submissions",
    paragraphs: [
      "When you submit an inquiry through the Website, you understand that the information you provide may be reviewed, evaluated, processed, and routed based on the campaign, eligibility criteria, geographic location, information provided, and other applicable criteria.",
      "Submitting an inquiry does not guarantee that:",
    ],
    bullets: [
      "You qualify for a particular claim or legal matter;",
      "You will receive compensation;",
      "You will be contacted;",
      "You will be accepted as a client;",
      "An attorney will represent you; or",
      "Any legal action will be taken on your behalf.",
    ],
    after: ["The submission of an inquiry does not create an attorney-client relationship."],
  },
  {
    title: "Referral and Third-Party Services",
    paragraphs: [
      "Depending on the campaign and the information provided, your inquiry may be shared with or referred to third parties that may evaluate or respond to your inquiry.",
      "These parties may include:",
    ],
    bullets: [
      "Lead buyers;",
      "Intake providers;",
      "Law firms;",
      "Attorneys or other legal professionals; and",
      "Service providers or other organizations involved in processing or responding to inquiries.",
    ],
    after: [
      "The identity and number of parties receiving an inquiry may vary depending on the applicable campaign, eligibility criteria, geographic location, and other factors.",
      "Once an inquiry is provided to a third party, that party's interactions with you are governed by its own policies, terms, and practices.",
      "Lawsuit Settlement Help does not control the services, advice, representations, eligibility decisions, or outcomes provided by third parties.",
    ],
  },
  {
    title: "No Attorney-Client Relationship",
    paragraphs: [
      "Submitting an inquiry through the Website, communicating with Lawsuit Settlement Help, or being contacted by a third party as a result of an inquiry does not create an attorney-client relationship.",
      "An attorney-client relationship can only be established directly between you and an attorney or law firm after the attorney or law firm has agreed to represent you.",
      "You should not rely on information available through the Website as a substitute for advice from a qualified attorney.",
    ],
  },
  {
    title: "No Guarantee of Legal Services or Results",
    paragraphs: ["Lawsuit Settlement Help does not guarantee that you will:"],
    bullets: [
      "Qualify for any particular legal matter;",
      "Be contacted by an attorney, law firm, intake provider, or other third party;",
      "Be accepted for representation;",
      "Receive a settlement or other compensation;",
      "Recover any particular amount; or",
      "Obtain any particular legal result.",
    ],
    after: [
      "Past results, references to settlements, or descriptions of legal matters, where presented, do not guarantee a similar result.",
      "Every legal matter depends on its specific facts and circumstances.",
    ],
  },
  {
    title: "User Information",
    paragraphs: [
      "You agree that information you submit through the Website is accurate and truthful to the best of your knowledge.",
      "You should not submit information that:",
    ],
    bullets: [
      "Is intentionally false or misleading;",
      "Belongs to another person without authorization;",
      "Contains malicious software or harmful code;",
      "Violates applicable law; or",
      "Is submitted for fraudulent, abusive, or unauthorized purposes.",
    ],
    after: ["You are responsible for the information you choose to submit through the Website."],
  },
  {
    title: "Communications and Consent",
    paragraphs: [
      "If you submit an inquiry and provide the applicable consent, you may be contacted regarding your inquiry by parties associated with the applicable campaign.",
      "Depending on the consent provided and applicable law, communications may include telephone calls, SMS/text messages, emails, or other communications.",
      "Please review the disclosures presented when submitting an inquiry.",
      "Providing consent to receive communications does not guarantee that you will receive any particular number or type of communications.",
    ],
  },
  {
    title: "Website Content",
    paragraphs: [
      "The Website and its content are provided for general informational purposes.",
      "We make reasonable efforts to maintain the Website, but we do not guarantee that:",
    ],
    bullets: [
      "The Website will always be available;",
      "Information will always be complete, accurate, or current;",
      "The Website will be free from errors or interruptions; or",
      "The Website will be free from viruses or other harmful components.",
    ],
    after: ["We may modify, suspend, or discontinue any portion of the Website at any time."],
  },
  {
    title: "Intellectual Property",
    paragraphs: [
      "Unless otherwise stated, the content of the Website, including text, graphics, logos, designs, images, layout, and other materials, is owned by or licensed to Lawsuit Settlement Help and is protected by applicable intellectual property laws.",
      "You may access and use the Website for personal, lawful purposes.",
      "You may not reproduce, distribute, modify, republish, sell, or commercially exploit Website content without appropriate authorization.",
    ],
  },
  {
    title: "Third-Party Websites and Services",
    paragraphs: [
      "The Website may contain links to websites or services operated by third parties.",
      "These links are provided for convenience and do not necessarily constitute an endorsement.",
      "Lawsuit Settlement Help does not control third-party websites, services, policies, or content and is not responsible for their availability, accuracy, privacy practices, or terms.",
      "Your interactions with third parties are between you and the applicable third party.",
    ],
  },
  {
    title: "Disclaimers",
    paragraphs: [
      "To the fullest extent permitted by applicable law, the Website and its content are provided on an “as is” and “as available” basis.",
      "Lawsuit Settlement Help disclaims warranties, express or implied, to the extent permitted by law, including warranties relating to accuracy, availability, fitness for a particular purpose, and non-infringement.",
      "Nothing in these Terms is intended to exclude or limit any rights or protections that cannot lawfully be excluded or limited.",
    ],
  },
  {
    title: "Limitation of Liability",
    paragraphs: [
      "To the fullest extent permitted by applicable law, Lawsuit Settlement Help and its owners, operators, employees, contractors, and service providers will not be liable for indirect, incidental, consequential, special, exemplary, or punitive damages arising from or relating to your use of the Website or interactions with third parties contacted through the Website.",
      "This includes, to the extent permitted by law, damages arising from reliance on information provided through the Website, third-party services, communications, or the inability to access or use the Website.",
      "Nothing in these Terms limits liability that cannot legally be limited or excluded under applicable law.",
    ],
  },
  {
    title: "Indemnification",
    paragraphs: [
      "To the extent permitted by applicable law, you agree to defend, indemnify, and hold harmless Lawsuit Settlement Help and its owners, operators, employees, contractors, and service providers from claims, liabilities, damages, losses, and expenses arising from your unlawful use of the Website, violation of these Terms, or intentional misuse of the Website.",
    ],
  },
  {
    title: "Privacy",
    paragraphs: [
      "Your use of the Website is also governed by our Privacy Policy, which explains how information submitted through the Website may be collected, used, maintained, and shared.",
      "Our Privacy Policy is incorporated into these Terms by reference.",
    ],
  },
  {
    title: "SMS and Messaging",
    paragraphs: [
      "Message frequency may vary, message and data rates may apply, and information regarding available opt-out methods and customer assistance will be provided as part of the messaging program and the disclosures presented when you submit an inquiry.",
    ],
  },
  {
    title: "Changes to These Terms",
    paragraphs: [
      "We may update these Terms from time to time.",
      "When changes are made, the updated Effective Date will be displayed at the beginning of the Terms.",
      "Your continued use of the Website after updated Terms are posted constitutes acceptance of the updated Terms to the extent permitted by applicable law.",
    ],
  },
  {
    title: "Governing Law",
    paragraphs: [
      "These Terms are governed by the Laws of India, without regard to conflict-of-law principles, except to the extent that applicable law requires otherwise.",
    ],
  },
  {
    title: "Severability",
    paragraphs: [
      "If any provision of these Terms is determined to be invalid or unenforceable, the remaining provisions will remain in effect to the fullest extent permitted by law.",
    ],
  },
  {
    title: "Entire Agreement",
    paragraphs: [
      "These Terms, together with the Privacy Policy and any other disclosures presented through the Website, constitute the terms governing your use of the Website and supersede prior statements concerning the same subject matter, except where otherwise required by applicable law.",
    ],
  },
  {
    title: "Contact",
    paragraphs: [
      "Questions regarding these Terms may be submitted using the contact information made available through the Website.",
    ],
  },
];

export default function TermsOfUse() {
  return (
    <main data-testid="terms-of-use-page" className="min-h-screen bg-[#FBF9F5] text-[#1A1715]">
      <div className="mx-auto max-w-3xl px-5 sm:px-10 py-20 sm:py-28">
        <Overline>Legal</Overline>
        <h1 className="mt-4 font-serif font-semibold text-4xl sm:text-5xl tracking-tight">
          Terms of Use
        </h1>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C]">
          Effective Date: August 20, 2026 · lawsuitsettlementhelp.com
        </p>
        <div className="mt-8 space-y-4">
          {PREAMBLE.map((p) => (
            <p key={p.slice(0, 24)} className="font-news text-base text-[#44403C] leading-relaxed">
              {p}
            </p>
          ))}
        </div>
        <div className="mt-12 space-y-10">
          {SECTIONS.map((s, i) => (
            <section key={s.title}>
              <h2 className="font-serif font-semibold text-2xl">
                <span className="font-serif italic text-[#B45309]/50 mr-3">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {s.title}
              </h2>
              {s.paragraphs?.map((p) => (
                <p key={p.slice(0, 24)} className="mt-3 font-news text-base text-[#44403C] leading-relaxed">
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul className="mt-3 space-y-1.5">
                  {s.bullets.map((b) => (
                    <li key={b.slice(0, 24)} className="flex gap-3 font-news text-base text-[#44403C] leading-relaxed">
                      <span className="text-[#C2410C] shrink-0">·</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.after?.map((p) => (
                <p key={p.slice(0, 24)} className="mt-3 font-news text-base text-[#44403C] leading-relaxed">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </div>
        <a
          href="/"
          data-testid="terms-back-link"
          className="mt-14 inline-block font-sans text-sm font-semibold text-[#C2410C] border-b border-[#C2410C] pb-1 hover:text-[#9A3412] transition-colors"
        >
          ← Back to homepage
        </a>
      </div>
    </main>
  );
}
